import React, { useEffect, useRef, useState } from "react";
import {
  QrCode,
  Upload,
  CheckCircle2,
  AlertCircle,
  Copy,
  Check,
  ShieldCheck,
  Sparkles,
  Smartphone,
  Save,
  RefreshCw,
  Info,
} from "lucide-react";

const API_BASE_URL = "https://api.vanyacollectionsrajahmundry.com";

const OnlinePaymentSettings = () => {
  const fileInputRef = useRef(null);

  const [upiId, setUpiId] = useState("");
  const [qrImage, setQrImage] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    loadPaymentSettings();
  }, []);

  const loadPaymentSettings = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/scanqr/settings`);
      const data = await response.json();

      if (data.success && data.settings) {
        setQrImage(data.settings.qr_image_url);
        setUpiId(data.settings.upi_id || "");
      }
    } catch (error) {
      console.error("Load payment settings error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setFeedback({ type: "error", message: "Please select a valid image file (PNG, JPG, WEBP)." });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setFeedback({ type: "error", message: "QR image must be less than 5 MB." });
      return;
    }

    setSelectedFile(file);
    const previewUrl = URL.createObjectURL(file);
    setQrImage(previewUrl);
    setFeedback({ type: "info", message: `Selected: ${file.name}. Click "Save Settings" to publish.` });
  };

  const handleCopyUpi = () => {
    if (!upiId) return;
    navigator.clipboard.writeText(upiId);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSave = async (e) => {
    if (e) e.preventDefault();

    if (!selectedFile && !qrImage) {
      setFeedback({ type: "error", message: "Please select a QR code image to upload." });
      return;
    }

    if (!upiId.trim()) {
      setFeedback({ type: "error", message: "Please enter your store's UPI ID." });
      return;
    }

    try {
      setSaving(true);
      setFeedback(null);

      const formData = new FormData();
      if (selectedFile) {
        formData.append("qrImage", selectedFile);
      }
      formData.append("upiId", upiId);

      const response = await fetch(`${API_BASE_URL}/scanqr/add`, {
        method: "POST",
        body: formData,
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || "Failed to save QR payment settings");
      }

      setQrImage(data.qrImageUrl);
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      setFeedback({
        type: "success",
        message: "UPI & QR Payment settings updated successfully!",
      });
      setTimeout(() => setFeedback(null), 5000);
    } catch (error) {
      console.error("Save QR error:", error);
      setFeedback({
        type: "error",
        message: error.message || "Failed to save QR payment settings",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={styles.container}>
      {/* HEADER */}
      <div style={styles.header}>
        <div>
          <div style={styles.badgeLabel}>
            <Sparkles size={13} color="#D4AF37" />
            <span>PAYMENT GATEWAY CONFIGURATION</span>
          </div>
          <h1 style={styles.title}>Online UPI & QR Settings</h1>
          <p style={styles.subtitle}>
            Manage the official UPI QR code and Virtual Payment Address (VPA) presented to customers during checkout.
          </p>
        </div>

        <button
          onClick={loadPaymentSettings}
          disabled={loading}
          style={styles.refreshBtn}
        >
          <RefreshCw size={15} className={loading ? "spin-icon" : ""} />
          <span>{loading ? "Refreshing..." : "Refresh"}</span>
        </button>
      </div>

      {/* FEEDBACK ALERT */}
      {feedback && (
        <div
          style={{
            ...styles.alert,
            backgroundColor:
              feedback.type === "success"
                ? "#ecfdf5"
                : feedback.type === "error"
                ? "#fef2f2"
                : "#eff6ff",
            borderColor:
              feedback.type === "success"
                ? "#a7f3d0"
                : feedback.type === "error"
                ? "#fecaca"
                : "#bfdbfe",
            color:
              feedback.type === "success"
                ? "#065f46"
                : feedback.type === "error"
                ? "#991b1b"
                : "#1e40af",
          }}
        >
          {feedback.type === "success" ? (
            <CheckCircle2 size={18} color="#059669" />
          ) : feedback.type === "error" ? (
            <AlertCircle size={18} color="#dc2626" />
          ) : (
            <Info size={18} color="#2563eb" />
          )}
          <span>{feedback.message}</span>
        </div>
      )}

      {/* MAIN TWO-COLUMN STUDIO */}
      <div className="qr-settings-grid" style={styles.gridStudio}>
        {/* LEFT COLUMN: CONFIGURATION CONTROLS */}
        <div className="qr-card-responsive" style={styles.card}>
          <div style={styles.cardHeader}>
            <div style={styles.cardHeaderIcon}>
              <QrCode size={20} color="#D4AF37" />
            </div>
            <div>
              <h2 style={styles.cardTitle}>Payment Configuration</h2>
              <p style={styles.cardSubtitle}>
                Ensure your UPI ID is exact so payment verification and manual orders match smoothly.
              </p>
            </div>
          </div>

          <form onSubmit={handleSave} style={styles.form}>
            {/* UPI ID Field */}
            <div style={styles.fieldGroup}>
              <label style={styles.label}>
                <span>Store UPI ID / VPA</span>
                <span style={styles.requiredStar}>*</span>
              </label>
              <div style={styles.inputWrapper}>
                <input
                  type="text"
                  value={upiId}
                  onChange={(e) => setUpiId(e.target.value)}
                  placeholder="e.g. vanyacollections@icici"
                  required
                  style={styles.input}
                />
                {upiId && (
                  <button
                    type="button"
                    onClick={handleCopyUpi}
                    title="Copy UPI ID"
                    style={styles.copyInlineBtn}
                  >
                    {copied ? <Check size={14} color="#059669" /> : <Copy size={14} color="#64748b" />}
                  </button>
                )}
              </div>
              <span style={styles.hint}>
                Customers scan the QR or copy this VPA address directly from the checkout page.
              </span>
            </div>

            {/* QR Upload Zone */}
            <div style={styles.fieldGroup}>
              <label style={styles.label}>
                <span>Upload New QR Image</span>
                <span style={styles.requiredStar}>*</span>
              </label>

              <div
                style={styles.dropZone}
                onClick={() => fileInputRef.current?.click()}
              >
                <div style={styles.dropZoneIcon}>
                  <Upload size={24} color="#14352b" />
                </div>
                <div style={styles.dropZoneTitle}>
                  {selectedFile ? selectedFile.name : "Click to browse and upload QR code image"}
                </div>
                <div style={styles.dropZoneSub}>
                  Supports PNG, JPG, JPEG, WEBP up to 5 MB
                </div>
                <button
                  type="button"
                  style={styles.browseBtn}
                  onClick={(e) => {
                    e.stopPropagation();
                    fileInputRef.current?.click();
                  }}
                >
                  Choose File
                </button>
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/webp"
                onChange={handleFileChange}
                style={{ display: "none" }}
              />
            </div>

            {/* Security note */}
            <div style={styles.securityNote}>
              <ShieldCheck size={20} color="#059669" style={{ flexShrink: 0 }} />
              <div>
                <strong style={{ display: "block", color: "#065f46", fontSize: "13px" }}>
                  Official Store Verification
                </strong>
                <span style={{ fontSize: "12px", color: "#047857" }}>
                  This QR code will be hosted on secure Cloudinary CDN and served via HTTPS to prevent tampering.
                </span>
              </div>
            </div>

            {/* Action Button */}
            <div style={styles.actionRow}>
              <button
                type="submit"
                disabled={saving}
                style={{
                  ...styles.saveBtn,
                  opacity: saving ? 0.75 : 1,
                  cursor: saving ? "not-allowed" : "pointer",
                }}
              >
                {saving ? (
                  <>
                    <RefreshCw size={17} className="spin-icon" />
                    <span>Saving Changes...</span>
                  </>
                ) : (
                  <>
                    <Save size={18} />
                    <span>Save Payment Settings</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>

        {/* RIGHT COLUMN: LIVE CHECKOUT PREVIEW */}
        <div className="qr-card-responsive" style={styles.previewCard}>
          <div style={styles.previewHeader}>
            <Smartphone size={18} color="#D4AF37" />
            <span>Customer Checkout Preview</span>
          </div>

          <div className="qr-preview-phone" style={styles.phoneMockup}>
            <div style={styles.phoneHeader}>
              <div style={styles.phoneBrand}>Vanya Collections</div>
              <div style={styles.phoneTag}>Scan & Pay via any UPI App</div>
            </div>

            <div className="qr-box-responsive" style={styles.qrDisplayBox}>
              {qrImage ? (
                <img
                  src={qrImage}
                  alt="Store Payment QR"
                  style={styles.qrImage}
                />
              ) : (
                <div style={styles.noQrPlaceholder}>
                  <QrCode size={64} color="#cbd5e1" />
                  <span style={{ marginTop: 10, fontSize: "13px", color: "#94a3b8" }}>
                    No QR code loaded
                  </span>
                </div>
              )}
            </div>

            <div style={styles.previewUpiBox}>
              <div style={styles.previewUpiLabel}>UPI ID / VPA</div>
              <div style={styles.previewUpiValue}>
                {upiId || "Not configured yet"}
              </div>
            </div>

            <div style={styles.acceptedApps}>
              <span>GPay</span> • <span>PhonePe</span> • <span>Paytm</span> • <span>BHIM UPI</span>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .spin-icon {
          animation: spin 1s linear infinite;
        }

        @media (max-width: 900px) {
          .qr-settings-grid {
            grid-template-columns: 1fr !important;
            display: flex !important;
            flex-direction: column !important;
            gap: 20px !important;
          }
          .qr-card-responsive {
            padding: 20px 16px !important;
            border-radius: 14px !important;
          }
          .qr-preview-phone {
            max-width: 100% !important;
            width: 100% !important;
            box-sizing: border-box !important;
          }
          .qr-box-responsive {
            width: 200px !important;
            height: 200px !important;
            max-width: 100% !important;
            margin: 0 auto 16px auto !important;
          }
        }
      `}</style>
    </div>
  );
};

const styles = {
  container: {
    maxWidth: "1100px",
    margin: "0 auto",
    fontFamily: "'Plus Jakarta Sans', system-ui, -apple-system, sans-serif",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: "24px",
    flexWrap: "wrap",
    gap: "16px",
  },
  badgeLabel: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    fontSize: "11px",
    fontWeight: "700",
    letterSpacing: "1.2px",
    color: "#D4AF37",
    backgroundColor: "#14352b",
    padding: "4px 10px",
    borderRadius: "20px",
    marginBottom: "8px",
  },
  title: {
    fontSize: "26px",
    fontWeight: "800",
    color: "#14352b",
    margin: "0 0 6px 0",
  },
  subtitle: {
    fontSize: "14px",
    color: "#64748b",
    margin: 0,
    maxWidth: "600px",
  },
  refreshBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    padding: "9px 16px",
    borderRadius: "10px",
    backgroundColor: "#ffffff",
    border: "1.5px solid #e2e8f0",
    color: "#14352b",
    fontSize: "13px",
    fontWeight: "600",
    cursor: "pointer",
  },
  alert: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "14px 18px",
    borderRadius: "10px",
    border: "1px solid",
    marginBottom: "22px",
    fontSize: "14px",
    fontWeight: "600",
  },
  gridStudio: {
    display: "grid",
    gridTemplateColumns: "1fr 380px",
    gap: "24px",
  },
  card: {
    backgroundColor: "#ffffff",
    borderRadius: "16px",
    padding: "28px",
    border: "1px solid #e2e8f0",
    boxShadow: "0 6px 18px rgba(0,0,0,0.04)",
  },
  cardHeader: {
    display: "flex",
    alignItems: "flex-start",
    gap: "14px",
    paddingBottom: "20px",
    marginBottom: "24px",
    borderBottom: "1px solid #f1f5f9",
  },
  cardHeaderIcon: {
    width: "40px",
    height: "40px",
    borderRadius: "10px",
    backgroundColor: "#14352b",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  cardTitle: {
    fontSize: "18px",
    fontWeight: "800",
    color: "#14352b",
    margin: "0 0 4px 0",
  },
  cardSubtitle: {
    fontSize: "13px",
    color: "#64748b",
    margin: 0,
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "20px",
  },
  fieldGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  label: {
    fontSize: "13px",
    fontWeight: "700",
    color: "#334155",
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },
  requiredStar: {
    color: "#ef4444",
  },
  inputWrapper: {
    position: "relative",
    display: "flex",
    alignItems: "center",
  },
  input: {
    width: "100%",
    padding: "12px 42px 12px 14px",
    borderRadius: "10px",
    border: "1.5px solid #cbd5e1",
    fontSize: "14px",
    color: "#0f172a",
    outline: "none",
    boxSizing: "border-box",
  },
  copyInlineBtn: {
    position: "absolute",
    right: "10px",
    background: "transparent",
    border: "none",
    cursor: "pointer",
    padding: "6px",
    display: "flex",
    alignItems: "center",
  },
  hint: {
    fontSize: "11px",
    color: "#94a3b8",
  },
  dropZone: {
    border: "2px dashed #cbd5e1",
    borderRadius: "12px",
    padding: "26px",
    textAlign: "center",
    cursor: "pointer",
    backgroundColor: "#f8fafc",
    transition: "border-color 0.2s ease",
  },
  dropZoneIcon: {
    width: "48px",
    height: "48px",
    borderRadius: "50%",
    backgroundColor: "#ecfdf5",
    border: "1px solid #a7f3d0",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: "10px",
  },
  dropZoneTitle: {
    fontSize: "14px",
    fontWeight: "700",
    color: "#1e293b",
    marginBottom: "4px",
  },
  dropZoneSub: {
    fontSize: "12px",
    color: "#64748b",
    marginBottom: "12px",
  },
  browseBtn: {
    padding: "6px 14px",
    borderRadius: "6px",
    backgroundColor: "#ffffff",
    border: "1px solid #cbd5e1",
    fontSize: "12px",
    fontWeight: "700",
    color: "#334155",
    cursor: "pointer",
  },
  securityNote: {
    display: "flex",
    alignItems: "flex-start",
    gap: "10px",
    backgroundColor: "#f0fdf4",
    border: "1px solid #bbf7d0",
    borderRadius: "10px",
    padding: "12px 14px",
  },
  actionRow: {
    marginTop: "8px",
  },
  saveBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    padding: "12px 28px",
    borderRadius: "10px",
    background: "linear-gradient(135deg, #14352b 0%, #1f4a3c 100%)",
    color: "#D4AF37",
    border: "none",
    fontSize: "14px",
    fontWeight: "800",
    boxShadow: "0 4px 14px rgba(20, 53, 43, 0.25)",
  },
  /* Preview Card */
  previewCard: {
    backgroundColor: "#ffffff",
    borderRadius: "16px",
    padding: "24px",
    border: "1px solid #e2e8f0",
    boxShadow: "0 6px 18px rgba(0,0,0,0.04)",
    display: "flex",
    flexDirection: "column",
  },
  previewHeader: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontSize: "13px",
    fontWeight: "700",
    color: "#14352b",
    marginBottom: "16px",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  phoneMockup: {
    backgroundColor: "#fafbfb",
    borderRadius: "14px",
    border: "1.5px solid #e2e8f0",
    padding: "20px",
    textAlign: "center",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  phoneHeader: {
    marginBottom: "16px",
  },
  phoneBrand: {
    fontSize: "15px",
    fontWeight: "800",
    color: "#14352b",
    letterSpacing: "0.5px",
  },
  phoneTag: {
    fontSize: "11px",
    color: "#64748b",
    marginTop: "2px",
  },
  qrDisplayBox: {
    width: "220px",
    height: "220px",
    backgroundColor: "#ffffff",
    borderRadius: "12px",
    border: "1px solid #e2e8f0",
    boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
    padding: "10px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: "16px",
  },
  qrImage: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
  },
  noQrPlaceholder: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  previewUpiBox: {
    width: "100%",
    backgroundColor: "#ffffff",
    border: "1px solid #edf2f7",
    borderRadius: "8px",
    padding: "10px",
    marginBottom: "14px",
  },
  previewUpiLabel: {
    fontSize: "10px",
    fontWeight: "700",
    color: "#94a3b8",
    textTransform: "uppercase",
    marginBottom: "3px",
  },
  previewUpiValue: {
    fontSize: "13px",
    fontWeight: "700",
    color: "#14352b",
    fontFamily: "monospace",
    wordBreak: "break-all",
  },
  acceptedApps: {
    fontSize: "11px",
    color: "#94a3b8",
    fontWeight: "600",
  },
};

export default OnlinePaymentSettings;